// How a raw subscription or instance state reads on a screen, in one place.
//
// Every portal surface that receives a raw `state` — Fleet, Home, All
// Customers' drill-in, Billing, the subscription status page, the activity
// line — draws its word and its badge tone from here, so the surfaces cannot
// disagree about what a state is called. The customer-facing Subscriptions
// pages receive `state_label` / `state_class` from the server instead, and
// StateBadge draws those; the server produces the same INTERNAL_LABEL string
// below, and a backend test pins it.
//
// The one rule that is not a table lookup: a LIVE INTERNAL-channel agreement —
// the one this service mints for a deployment metricsIQ stands up itself —
// reads "Internal, not billable" whatever its stored state. The internal
// channel has no adapter and no activation, so its agreements are created
// `pending_activation` and stay there; "Pending activation" describes a
// marketplace lifecycle the tenant is not in, and every customer metricsIQ
// runs itself read as waiting on setup until 2026-10-09. The stored state is
// not rewritten (that would invent a lifecycle event); the WORD is.
//
// An ENDED agreement is over on every channel, and over wins: the internal
// rule describes what a live agreement is not (billable), and an agreement
// that has ended is not live. Without this, retiring an internal deployment
// left its agreement reading as if it were still standing (nnpc-nuims,
// 2026-10-09). Like a decommissioned instance, it carries the day it ended —
// "Ended · <day>" — where the row has one.
//
// And a decommissioned instance reads "Decommissioned · <day>", with the day
// its retirement was decided — the date the row carries — rather than a bare
// word beside a retirement badge saying the same thing twice.

import { onDay } from './instants';
import type { Retired } from './reads';

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export interface Shown {
  variant: BadgeVariant;
  label: string;
}

/** The channel this service mints agreements on for its own deployments. */
export const INTERNAL_CHANNEL = 'internal';

/** The same string the server sends as `state_label` for an internal row. */
export const INTERNAL_LABEL = 'Internal, not billable';

/** Whether a subscription is waiting on setup. Matches SubscriptionState. */
export const PENDING_ACTIVATION = 'pending_activation';

const DECOMMISSIONED = 'decommissioned';
const ENDED = 'ended';

const INSTANCE_STATE: Record<string, Shown> = {
  running: { variant: 'default', label: 'Running' },
  provisioning: { variant: 'outline', label: 'Provisioning' },
  stopped: { variant: 'secondary', label: 'Stopped' },
  decommissioned: { variant: 'secondary', label: 'Decommissioned' },
};

const SUBSCRIPTION_STATE: Record<string, Shown> = {
  active: { variant: 'default', label: 'Active' },
  pending_activation: { variant: 'outline', label: 'Pending activation' },
  suspended: { variant: 'secondary', label: 'Suspended' },
  ending: { variant: 'outline', label: 'Ending' },
  ended: { variant: 'secondary', label: 'Ended' },
  activation_failed: { variant: 'destructive', label: 'Activation failed' },
};

/** A state the tables do not know reads as itself, so nothing is hidden. */
function fromTable(table: Record<string, Shown>, state: string): Shown {
  return table[state] ?? { variant: 'outline', label: state };
}

export function isInternal(channel: string | null | undefined): boolean {
  return channel === INTERNAL_CHANNEL;
}

/**
 * How an agreement reads: ended wins, then the internal rule, then the
 * state table. An ended agreement carries the day it ended where the row
 * has one; one with no `ended_at` on record reads as the bare word rather
 * than an invented date.
 */
export function agreementShown({
  channel,
  state,
  ended_at,
}: {
  channel: string;
  state: string;
  ended_at?: string | null;
}): Shown {
  if (state === ENDED) {
    const shown = fromTable(SUBSCRIPTION_STATE, state);
    return ended_at ? { ...shown, label: `${shown.label} · ${onDay(ended_at)}` } : shown;
  }
  if (isInternal(channel)) {
    return { variant: 'secondary', label: INTERNAL_LABEL };
  }
  return fromTable(SUBSCRIPTION_STATE, state);
}

/**
 * How an instance reads. Decommissioned carries the day it was retired,
 * where the row has one; a decommissioned instance with no retirement on
 * record reads as the bare word rather than an invented date.
 */
export function instanceShown({ state, retired }: { state: string; retired?: Retired | null }): Shown {
  const shown = fromTable(INSTANCE_STATE, state);
  if (state === DECOMMISSIONED && retired) {
    return { ...shown, label: `${shown.label} · ${onDay(retired.at)}` };
  }
  return shown;
}

/**
 * Whether an agreement is waiting on somebody to set it up. A marketplace
 * subscription pending activation is; an internal one in the same stored
 * state is not — nothing will ever activate it, and offering "Finish setup"
 * for it is offering a step that does not exist.
 */
export function isWaitingOnActivation(entry: { channel?: string | null; state?: string | null }): boolean {
  return entry.state === PENDING_ACTIVATION && !isInternal(entry.channel);
}
